import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, createTextVNode as _createTextVNode, unref as _unref, resolveComponent as _resolveComponent, mergeProps as _mergeProps, createVNode as _createVNode, withCtx as _withCtx, createBlock as _createBlock, vModelText as _vModelText, withKeys as _withKeys, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "station-form-category-container flex flex-col gap-y-2 pb-6" }
const _hoisted_2 = /*#__PURE__*/_createElementVNode("span", { class: "station-form-category-title font-bold" }, "Comentários", -1)
const _hoisted_3 = { class: "flex flex-col gap-3" }
const _hoisted_4 = {
  key: 0,
  class: "text-gray-500 text-sm text-left"
}
const _hoisted_5 = {
  key: 1,
  "data-testid": "comments-load-error",
  class: "text-negative text-sm text-left flex items-center gap-2"
}
const _hoisted_6 = {
  key: 2,
  class: "text-gray-500 text-sm text-left"
}
const _hoisted_7 = { class: "text-start" }
const _hoisted_8 = { class: "font-bold text-sm" }
const _hoisted_9 = { class: "text-xs font-normal text-gray-500" }
const _hoisted_10 = { class: "text-sm mt-1 text-left" }
const _hoisted_11 = {
  key: 0,
  class: "flex border rounded px-3 py-3 gap-4 items-start mt-2"
}
const _hoisted_12 = ["onKeyup"]

import { ref, watch, onMounted, PropType } from 'vue'
import { format, subHours } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useToast } from 'vue-toastification'
import { mdiTrashCanOutline } from '@mdi/js'
import Network from '@/Network'
import { useErrorToast } from '@/composables/useErrorToast'
import { getApiErrorMessage } from '@/utils/apiError'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import { useUserStore } from '@/pinia-store/useUsersStore'

export interface CommentItem {
  id?: string | number
  author: string
  text: string
  date: string
}


export default /*#__PURE__*/_defineComponent({
  __name: 'CommentsForm',
  props: {
  comments: {
    type: Array as PropType<CommentItem[]>,
    default: () => []
  },
  currentUser: {
    type: String,
    default: undefined
  },
  attendanceId: {
    type: [Number, String] as PropType<number | string | undefined>,
    default: undefined
  },
  /**
   * Criar/remover comentário é escrita (o backend exige `attendance_emitting`).
   * `false` deixa os comentários só para leitura — caso do perfil Extrator.
   */
  canComment: {
    type: Boolean,
    default: true
  }
},
  setup(__props) {

const props = __props;



const toast = useToast()
const { notifyError } = useErrorToast()
const userStore = useUserStore()
const draftComment = ref('')
const localComments = ref<CommentItem[]>([...props.comments])
const isLoading = ref(false)
const loadError = ref<string | null>(null)
const isSaving = ref(false)
const deletingId = ref<string | number | undefined>(undefined)

function formatCommentDate(isoDate: string): string {
  try {
    // API retorna createdAt em UTC sem indicador de timezone; subtrai 3h para converter a UTC-3 (Brasília)
    return format(subHours(new Date(isoDate), 3), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })
  } catch {
    return isoDate
  }
}

async function fetchComments() {
  if (!props.attendanceId) return
  isLoading.value = true
  loadError.value = null
  try {
    const response = await Network.dsAttendance.listComments(props.attendanceId)
    localComments.value = response.data.map((item) => ({
      id: item.id,
      author: item.author.name,
      text: item.text,
      date: formatCommentDate(item.createdAt)
    }))
  } catch (error: unknown) {
    const fallback = 'Não foi possível carregar os comentários.'
    loadError.value = getApiErrorMessage(error, fallback)
    notifyError(error, fallback)
  } finally {
    isLoading.value = false
  }
}

watch(
  () => props.comments,
  (newComments) => {
    if (!props.attendanceId) localComments.value = [...newComments]
  },
  { deep: true }
)

onMounted(() => {
  if (props.attendanceId) {
    fetchComments()
  }
})

const handleSaveDraft = async () => {
  const text = draftComment.value.trim()
  if (!text) return

  if (props.attendanceId) {
    isSaving.value = true
    try {
      await Network.dsAttendance.createComment(props.attendanceId, { text })
      draftComment.value = ''
      await fetchComments()
      toast.success('Comentário adicionado com sucesso!')
    } catch (error: unknown) {
      notifyError(error, 'Não foi possível adicionar o comentário.')
    } finally {
      isSaving.value = false
    }
    return
  }

  const currentDate = new Date()
    .toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
    .replace(',', ' às')

  localComments.value.unshift({
    id: Date.now(),
    author: props.currentUser ?? userStore.userData?.name ?? 'Usuário',
    text,
    date: currentDate
  })
  draftComment.value = ''
  toast.success('Comentário adicionado com sucesso!')
}

const handleDelete = async (comment: CommentItem, index: number) => {
  if (props.attendanceId && comment.id) {
    deletingId.value = comment.id
    try {
      await Network.dsAttendance.deleteComment(props.attendanceId, comment.id)
      localComments.value.splice(index, 1)
      toast.success('Comentário removido com sucesso!')
    } catch (error: unknown) {
      notifyError(error, 'Não foi possível remover o comentário.')
    } finally {
      deletingId.value = undefined
    }
    return
  }

  if (confirm('Tem certeza que deseja remover este comentário?')) {
    localComments.value.splice(index, 1)
    toast.success('Comentário removido com sucesso!')
  }
}

return (_ctx: any,_cache: any) => {
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_tooltip = _resolveComponent("v-tooltip")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _hoisted_2,
    _createElementVNode("div", _hoisted_3, [
      (isLoading.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_4, "Carregando comentários..."))
        : (loadError.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
              _createElementVNode("span", null, _toDisplayString(loadError.value), 1),
              _createElementVNode("button", {
                type: "button",
                class: "underline font-bold",
                onClick: fetchComments
              }, " Tentar novamente ")
            ]))
          : (localComments.value.length === 0)
            ? (_openBlock(), _createElementBlock("div", _hoisted_6, " Nenhum comentário registrado. "))
            : _createCommentVNode("", true),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(localComments.value, (comment, index) => {
        return (_openBlock(), _createElementBlock("div", {
          key: comment.id || index,
          class: "bg-gray-100 p-3 rounded-lg flex justify-between items-start"
        }, [
          _createElementVNode("div", _hoisted_7, [
            _createElementVNode("p", _hoisted_8, [
              _createTextVNode(_toDisplayString(comment.author) + " ", 1),
              _createElementVNode("span", _hoisted_9, " | " + _toDisplayString(comment.date), 1)
            ]),
            _createElementVNode("p", _hoisted_10, _toDisplayString(comment.text), 1)
          ]),
          (__props.canComment)
            ? (_openBlock(), _createBlock(_component_v_tooltip, {
                key: 0,
                text: "Remover"
              }, {
                activator: _withCtx(({ props }) => [
                  _createVNode(_component_v_btn, _mergeProps(props, {
                    color: "error",
                    variant: "text",
                    icon: _unref(mdiTrashCanOutline),
                    density: "compact",
                    size: "small",
                    loading: deletingId.value === comment.id,
                    onClick: ($event: any) => (handleDelete(comment, index))
                  }), null, 16, ["icon", "loading", "onClick"])
                ]),
                _: 2
              }, 1024))
            : _createCommentVNode("", true)
        ]))
      }), 128))
    ]),
    (__props.canComment)
      ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
          _withDirectives(_createElementVNode("input", {
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((draftComment).value = $event)),
            type: "text",
            placeholder: "Digite um comentário...",
            class: "flex-grow border-none outline-none text-sm placeholder-gray-500 bg-transparent",
            onKeyup: _withKeys(handleSaveDraft, ["enter"])
          }, null, 40, _hoisted_12), [
            [_vModelText, draftComment.value]
          ]),
          _createVNode(OutlineButton, {
            type: "button",
            color: "primary30",
            width: "200px",
            height: "32px",
            disabled: !draftComment.value.trim(),
            loading: isSaving.value,
            onClick: handleSaveDraft
          }, {
            default: _withCtx(() => [
              _createTextVNode(" Adicionar comentário ")
            ]),
            _: 1
          }, 8, ["disabled", "loading"])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})